import React, { useEffect, useState } from "react";
import { api, unwrapArray } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/context/AuthContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ArrowDown, ArrowUp, Edit, Loader2, Plus, Power, X } from "lucide-react";
import type { MasterLookupRecord } from "@/types";
import type { ProcessRecord, RouteStep } from "@/types/production";

const field = "w-full h-9 rounded-lg border border-input bg-background px-3 text-sm focus:outline-hidden focus:ring-2 focus:ring-ring";

interface Step {
  process_code: string;
  days: string;
}

export function ProcessesPage() {
  const { success, error } = useToast();
  const { hasPermission } = useAuth();
  const canSetup = hasPermission("production.setup");
  const [processes, setProcesses] = useState<ProcessRecord[]>([]);
  const [types, setTypes] = useState<MasterLookupRecord[]>([]);
  const [type, setType] = useState("");
  const [steps, setSteps] = useState<Step[]>([]);
  const [addCode, setAddCode] = useState("");
  const [savingRoute, setSavingRoute] = useState(false);
  const [dialog, setDialog] = useState<ProcessRecord | "new" | null>(null);
  const [form, setForm] = useState({ name: "", code: "", default_days: "1" });
  const [saving, setSaving] = useState(false);

  const loadProcesses = () => api.get("/api/ProcessMaster/List?include_closed=true").then((r) => setProcesses(unwrapArray<ProcessRecord>(r))).catch((err: any) => error("Fetch Error", err.message || "Failed to load processes"));
  useEffect(() => {
    loadProcesses();
    api.get("/api/MasterLookup/ByCategory/PRODUCT_TYPE").then((r) => {
      const list = unwrapArray<MasterLookupRecord>(r);
      setTypes(list);
      setType((t) => t || list.find((x) => x.code === "FABRIC")?.code || list[0]?.code || "");
    });
  }, []);

  useEffect(() => {
    if (!type) return;
    api.get(`/api/ProcessMaster/RouteFor/${type}`).then((r) => setSteps(unwrapArray<RouteStep>(r).map((s) => ({ process_code: s.process_code, days: String(s.days) })))).catch(() => setSteps([]));
  }, [type]);

  const nameOf = (c: string) => processes.find((p) => p.code === c)?.name || c;
  const move = (i: number, d: number) =>
    setSteps((s) => {
      const n = [...s];
      const j = i + d;
      if (j < 0 || j >= n.length) return s;
      [n[i], n[j]] = [n[j]!, n[i]!];
      return n;
    });

  const saveRoute = async () => {
    setSavingRoute(true);
    try {
      await api.put("/api/ProcessMaster/SaveRoute", { product_type: type, steps: steps.map((s) => ({ process_code: s.process_code, days: s.days ? Number(s.days) : undefined })) });
      success("Route saved", "New work orders of this product type follow it");
    } catch (err: any) {
      error("Could not save the route", err.message || "Please try again");
    } finally {
      setSavingRoute(false);
    }
  };

  useEffect(() => {
    if (!dialog) return;
    setForm(dialog === "new" ? { name: "", code: "", default_days: "1" } : { name: dialog.name, code: dialog.code, default_days: String(dialog.default_days) });
  }, [dialog]);

  const saveProcess = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) return error("Validation Error", "Enter the process name");
    setSaving(true);
    try {
      const body = { name: form.name.trim(), code: form.code.trim() || undefined, default_days: Number(form.default_days) || 1 };
      if (dialog && dialog !== "new") await api.put(`/api/ProcessMaster/Update/${dialog.id}`, body);
      else await api.post("/api/ProcessMaster/Insert", body);
      success("Process saved", "");
      setDialog(null);
      loadProcesses();
    } catch (err: any) {
      error("Could not save", err.message || "Please try again");
    } finally {
      setSaving(false);
    }
  };
  const setActive = async (p: ProcessRecord) => {
    try {
      await api.patch(`/api/ProcessMaster/SetActive/${p.id}`, { active: !p.status });
      loadProcesses();
    } catch (err: any) {
      error("Could not change", err.message || "Please try again");
    }
  };

  const unused = processes.filter((p) => p.status && !steps.some((s) => s.process_code === p.code));
  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Production processes</h1>
          <p className="text-sm text-muted-foreground mt-0.5">The stages work orders go through, and the usual sequence for each product type</p>
        </div>
        {canSetup && (
          <Button size="sm" onClick={() => setDialog("new")} className="h-9 gap-1.5 shadow-sm">
            <Plus className="size-4" /> Add process
          </Button>
        )}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <div className="px-4 py-3 border-b text-sm font-bold">Processes</div>
          <ul className="divide-y">
            {processes.map((p) => (
              <li key={p.id} className={`px-4 py-2.5 flex items-center gap-3 ${p.status ? "" : "opacity-60"}`}>
                <div className="flex-1">
                  <div className="text-sm font-semibold flex items-center gap-2">{p.name} {!p.status && <Badge variant="outline">Closed</Badge>}</div>
                  <div className="text-[11px] text-muted-foreground"><code className="font-mono">{p.code}</code> · usually {p.default_days} day{p.default_days === 1 ? "" : "s"}</div>
                </div>
                {canSetup && (
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" className="size-8" title="Edit" onClick={() => setDialog(p)}><Edit className="size-4" /></Button>
                    <Button variant="ghost" size="icon" className="size-8" title={p.status ? "Close" : "Reopen"} onClick={() => setActive(p)}><Power className="size-4" /></Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-xl border border-border bg-card overflow-hidden">
          <div className="px-4 py-3 border-b flex items-center justify-between gap-3">
            <div className="text-sm font-bold">Route for</div>
            <select className={`${field} max-w-[220px]`} value={type} onChange={(e) => setType(e.target.value)}>
              {types.map((t) => (
                <option key={t.code} value={t.code}>{t.name}</option>
              ))}
            </select>
          </div>
          <div className="p-4 space-y-3">
            <p className="text-xs text-muted-foreground">New work orders for this product type start with these stages, in this order. Each can still be changed on the work order.</p>
            <div className="rounded-lg border border-border divide-y divide-border">
              {steps.map((s, i) => (
                <div key={s.process_code} className="flex items-center gap-2 px-3 py-1.5 text-xs">
                  <span className="w-5 text-muted-foreground">{i + 1}</span>
                  <span className="flex-1 font-medium">{nameOf(s.process_code)}</span>
                  <Input className="h-7 w-16 text-xs" inputMode="numeric" value={s.days} onChange={(e) => setSteps(steps.map((x, j) => (j === i ? { ...x, days: e.target.value } : x)))} disabled={!canSetup} aria-label="Days" />
                  <span className="text-muted-foreground">days</span>
                  {canSetup && (
                    <>
                      <button type="button" className="text-muted-foreground hover:text-foreground" onClick={() => move(i, -1)}><ArrowUp className="size-3.5" /></button>
                      <button type="button" className="text-muted-foreground hover:text-foreground" onClick={() => move(i, 1)}><ArrowDown className="size-3.5" /></button>
                      <button type="button" className="text-muted-foreground hover:text-destructive" onClick={() => setSteps(steps.filter((_, j) => j !== i))}><X className="size-3.5" /></button>
                    </>
                  )}
                </div>
              ))}
              {steps.length === 0 && <p className="px-3 py-3 text-xs text-muted-foreground">No route yet. Work orders for this type need their stages chosen by hand.</p>}
            </div>
            {canSetup && (
              <>
                <div className="flex gap-2">
                  <select className={field} value={addCode} onChange={(e) => setAddCode(e.target.value)}>
                    <option value="">Add a stage…</option>
                    {unused.map((p) => (
                      <option key={p.code} value={p.code}>{p.name}</option>
                    ))}
                  </select>
                  <Button type="button" size="sm" variant="outline" className="h-9 gap-1" disabled={!addCode} onClick={() => { const p = processes.find((x) => x.code === addCode); setSteps([...steps, { process_code: addCode, days: String(p?.default_days || 1) }]); setAddCode(""); }}>
                    <Plus className="size-3.5" /> Add
                  </Button>
                </div>
                <Button size="sm" onClick={saveRoute} disabled={savingRoute} className="gap-1.5">
                  {savingRoute && <Loader2 className="size-4 animate-spin" />}
                  Save route
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      <Dialog open={!!dialog} onOpenChange={(o) => !o && !saving && setDialog(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{dialog && dialog !== "new" ? `Edit ${dialog.name}` : "Add process"}</DialogTitle>
            <DialogDescription>A kind of production stage, like Calendering or Embroidery.</DialogDescription>
          </DialogHeader>
          <form onSubmit={saveProcess} className="space-y-3 py-2 text-sm">
            <div className="space-y-1.5"><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-10" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Code</Label><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="Auto" disabled={!!dialog && dialog !== "new"} className="h-10" /></div>
              <div className="space-y-1.5"><Label>Usual days</Label><Input inputMode="numeric" value={form.default_days} onChange={(e) => setForm({ ...form, default_days: e.target.value })} className="h-10" /></div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialog(null)} disabled={saving}>Cancel</Button>
              <Button type="submit" disabled={saving} className="gap-1.5">
                {saving && <Loader2 className="size-4 animate-spin" />}
                Save
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
